// 幾何の基本型。座標はすべて「文書ピクセル座標」(左上原点、y 下向き) で扱う。
// 表示ズーム・パンは uiStore の ViewState だけが持ち、ここには混ぜない。

export interface Pt {
  x: number
  y: number
}

export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

export function rectIsEmpty(r: Rect | null | undefined): boolean {
  return !r || r.w <= 0 || r.h <= 0
}

export function rectIntersect(a: Rect, b: Rect): Rect | null {
  const x0 = Math.max(a.x, b.x)
  const y0 = Math.max(a.y, b.y)
  const x1 = Math.min(a.x + a.w, b.x + b.w)
  const y1 = Math.min(a.y + a.h, b.y + b.h)
  if (x1 <= x0 || y1 <= y0) return null
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }
}

export function rectUnion(a: Rect | null, b: Rect | null): Rect | null {
  if (!a) return b
  if (!b) return a
  const x0 = Math.min(a.x, b.x)
  const y0 = Math.min(a.y, b.y)
  const x1 = Math.max(a.x + a.w, b.x + b.w)
  const y1 = Math.max(a.y + a.h, b.y + b.h)
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }
}

export function rectEquals(a: Rect | null, b: Rect | null): boolean {
  if (!a || !b) return a === b
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h
}

export function rectTranslate(r: Rect, dx: number, dy: number): Rect {
  return { x: r.x + dx, y: r.y + dy, w: r.w, h: r.h }
}

/** 3x3 行列 (列優先、WebGL の mat3 と同じ並び)。 */
export type Mat3 = [number, number, number, number, number, number, number, number, number]

export const MAT3_IDENTITY: Mat3 = [1, 0, 0, 0, 1, 0, 0, 0, 1]

export function mat3Multiply(a: Mat3, b: Mat3): Mat3 {
  const out = new Array(9).fill(0) as Mat3
  for (let col = 0; col < 3; col++) {
    for (let row = 0; row < 3; row++) {
      let s = 0
      for (let k = 0; k < 3; k++) s += a[k * 3 + row]! * b[col * 3 + k]!
      out[col * 3 + row] = s
    }
  }
  return out
}

export function mat3Translate(tx: number, ty: number): Mat3 {
  return [1, 0, 0, 0, 1, 0, tx, ty, 1]
}

export function mat3Scale(sx: number, sy: number): Mat3 {
  return [sx, 0, 0, 0, sy, 0, 0, 0, 1]
}

/** 3×3 行列を点に適用する (射影成分があれば w で割る) */
export function mat3Apply(m: Mat3, p: Pt): Pt {
  const w = m[2] * p.x + m[5] * p.y + m[8]
  const iw = w === 1 || w === 0 ? 1 : 1 / w
  return {
    x: (m[0] * p.x + m[3] * p.y + m[6]) * iw,
    y: (m[1] * p.x + m[4] * p.y + m[7]) * iw
  }
}

/**
 * 矩形 src の 4 隅 (左上, 右上, 右下, 左下) を quad へ写す射影変換 (ホモグラフィ)。
 * 退化していれば null。
 */
export function homographyFromQuad(src: Rect, quad: [Pt, Pt, Pt, Pt]): Mat3 | null {
  // 単位正方形 → quad
  const [p0, p1, p2, p3] = quad
  const dx1 = p1.x - p2.x, dx2 = p3.x - p2.x, dx3 = p0.x - p1.x + p2.x - p3.x
  const dy1 = p1.y - p2.y, dy2 = p3.y - p2.y, dy3 = p0.y - p1.y + p2.y - p3.y
  let unit: Mat3
  if (Math.abs(dx3) < 1e-9 && Math.abs(dy3) < 1e-9) {
    unit = [p1.x - p0.x, p1.y - p0.y, 0, p2.x - p1.x, p2.y - p1.y, 0, p0.x, p0.y, 1]
  } else {
    const det = dx1 * dy2 - dx2 * dy1
    if (Math.abs(det) < 1e-12) return null
    const g = (dx3 * dy2 - dx2 * dy3) / det
    const h = (dx1 * dy3 - dx3 * dy1) / det
    unit = [p1.x - p0.x + g * p1.x, p1.y - p0.y + g * p1.y, g, p3.x - p0.x + h * p3.x, p3.y - p0.y + h * p3.y, h, p0.x, p0.y, 1]
  }
  // src 矩形 → 単位正方形
  const toUnit: Mat3 = [1 / src.w, 0, 0, 0, 1 / src.h, 0, -src.x / src.w, -src.y / src.h, 1]
  return mat3Multiply(unit, toUnit)
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}
